import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SelectOption } from '@/core/types/angular-primeng-api';
import { DataUtils } from '@/core/utils/data-utils';

class CrmWebhookClient {
    private static instance: CrmWebhookClient | null = null;
    // members for making up the webhooks url:
    private readonly baseUrl: string;
    private readonly webhooks: Map<CrmWebhookEvent, string>;

    constructor(url: string, webhooks: Map<CrmWebhookEvent, string>) {
        this.baseUrl = url;
        this.webhooks = webhooks;
    }

    private getWebhookUrl(event: CrmWebhookEvent): string {
        return this.baseUrl + this.webhooks.get(event);
    }

    public webhookExists(event: CrmWebhookEvent): boolean {
        return this.webhooks.has(event);
    }

    public static get(): CrmWebhookClient {
        if (this.instance == null) {
            // Note: The environment does not change, hence the caching of instance is fine as follows:
            if (environment.production) {
                const baseUrl = 'https://store.timebuzzer.com/?fluentcrm=1&route=contact&hash=';
                const webhooks = new Map<CrmWebhookEvent, string>([
                    [CrmWebhookEvent.startTrial15days, '7c7027ca-c146-4cd7-8e03-222935384ec8'],
                    [CrmWebhookEvent.startTrial30days, '9beaf85d-5469-48e7-b9ae-63a1ab4aee82'],
                    [CrmWebhookEvent.updateProfile, '6f9e9283-3759-4b41-ba70-2c9bd0d70224'],
                    [CrmWebhookEvent.onboardingWizardCompleted, '553c48de-3c6c-417b-a280-ea0872170fb2'],
                    [CrmWebhookEvent.createSubuser, '573e622d-29fa-4dcd-b951-9b00f64502fc'],
                    [CrmWebhookEvent.createAccountWithoutNewsletter, '4400f599-7f17-41aa-a264-3757c51cb5a8'],
                ]);
                this.instance = new CrmWebhookClient(baseUrl, webhooks);
            } else {
                const baseUrl = 'https://devstore.timebuzzer.com/?fluentcrm=1&route=contact&hash=';
                const webhooks = new Map<CrmWebhookEvent, string>([
                    [CrmWebhookEvent.startTrial15days, 'd9fab882-395f-44b7-b5e1-7221d4753906'],
                    [CrmWebhookEvent.startTrial30days, '06efe520-5743-4d24-a816-02753bcc4fa6'],
                    [CrmWebhookEvent.updateProfile, 'a4a5ecf3-abb7-4c7a-9dba-5485c826a504'],
                    [CrmWebhookEvent.onboardingWizardCompleted, 'c24bbf7c-1974-4bbc-afcd-e8f3cdbfe95f'],
                    [CrmWebhookEvent.createSubuser, '19855c09-db8b-4b17-a9c2-df41800b5318'],
                    [CrmWebhookEvent.createAccountWithoutNewsletter, 'da5e31de-4803-4ae2-9e8f-48761b045bb2'],
                ]);
                this.instance = new CrmWebhookClient(baseUrl, webhooks);
            }
        }
        return this.instance;
    }

    public async send(httpClient: HttpClient, event: CrmWebhookEvent, body: CrmBody): Promise<void> {
        const url = this.getWebhookUrl(event);
        return firstValueFrom(httpClient.post<void>(url, body));
    }
}

export enum CrmWebhookEvent {
    startTrial15days = 'startTrial15days',
    startTrial30days = 'startTrial30days',
    updateProfile = 'updateProfile',
    onboardingWizardCompleted = 'onboardingWizardCompleted',
    createSubuser = 'createSubuser',
    createAccountWithoutNewsletter = 'createAccountWithoutNewsletter',
}

export type CrmBody = {
    email?: string; // TODO the email field is actually mandatory, changing this would need adjustments all over the code...
    cloud_account_id?: number;
    last_login_webconsole?: string;
    language?: string;
    company?: string;
    first_name?: string;
    last_name?: string;
    company_size?: SelectOption;
    sector?: SelectOption;
};

@Injectable({
    providedIn: 'root',
})
export class CrmWebhookService {
    constructor(private readonly httpClient: HttpClient) {}

    /**
     * Sends a request to the CRM webhook.
     * This is a fire-and-forget method. Errors are not thrown, only logged.
     * @param event The event to be triggered (defines the webhook endpoint)
     * @param body The customer data to be transmitted.
     * @returns A promise that always resolves to void. You can use it to execute code after the request or simply ignore it.
     */
    public async send(event: CrmWebhookEvent, body: CrmBody): Promise<void> {
        if (!environment.crmEnabled) {
            console.debug('CrmWebhookService.send(): CRM disabled.');
            return;
        }
        if (!this.isBodyValid(body)) {
            console.error('CrmWebhookService.send(): Invalid body for CRM send request', body);
            return;
        }
        const client = CrmWebhookClient.get();
        if (!client.webhookExists(event)) {
            console.error('CrmWebhookService.send(): The provided event has no associated webhook registered.', event);
            return;
        }
        try {
            await client.send(this.httpClient, event, body);
        } catch (error) {
            console.error('CrmWebhookService.send(): Failed sending CRM webhook', error);
        }
    }

    private isBodyValid(body: unknown): boolean {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return body != null && DataUtils.isNotEmptyString((body as any).email);
    }
}
